/**
 * HTTP API newfungame: все эндпоинты — в одной таблице {@link endpoints} (метод, путь, схемы запроса и ответа).
 * Клиент (I2) и сервер (S1–S4) берут типы отсюда; заглушка сервера (`apps/server/src/api/stub/`) и фейковый
 * транспорт клиента (`apps/client/src/api/fake/`) обязаны покрыть каждый эндпоинт — это проверяет TypeScript.
 *
 * Соглашения:
 * - всё под `/api`, тела — JSON; у GET тела нет (схема запроса — пустой объект);
 * - сессия — cookie `nfg_session` (`HttpOnly; Secure; SameSite=None; Partitioned` — иначе во фрейме Telegram Web
 *   cookie не уходит, практика Sapero); вход создаёт или продлевает её;
 * - ошибка — HTTP 4xx/5xx и тело {@link ApiErrorBody}; код ошибки — {@link ApiErrorCode}, статус — {@link ERROR_STATUS};
 * - время — мс Unix (`UnixMs`), монеты — целые.
 */
import type { z } from "zod";
import { authEndpoints } from "./endpoints/auth";
import { betaEndpoints } from "./endpoints/beta";
import { economyEndpoints } from "./endpoints/economy";
import { leagueEndpoints } from "./endpoints/league";
import { liveEndpoints } from "./endpoints/live";
import { mapEndpoints } from "./endpoints/map";
import { paymentEndpoints } from "./endpoints/payments";
import { playerEndpoints } from "./endpoints/player";
import { shareEndpoints } from "./endpoints/share";
import { waveEndpoints } from "./endpoints/waves";

export * from "./endpoints/auth";
export * from "./endpoints/beta";
export * from "./endpoints/economy";
export * from "./endpoints/league";
export * from "./endpoints/live";
export * from "./endpoints/map";
export * from "./endpoints/payments";
export * from "./endpoints/player";
export * from "./endpoints/share";
export * from "./endpoints/waves";
export * from "./errors";
export * from "./player";
export * from "./player/league";
export * from "./player/beta";
export * from "./player/progress";
export * from "./player/season";
export * from "./endpoint";
export type { AdminWaveReplay } from "./admin";

/** Все эндпоинты API. Новый эндпоинт — строка в `endpoints/<домен>.ts`, затем реализация в server и транспорт в client. */
export const endpoints = {
  ...authEndpoints,
  ...betaEndpoints,
  ...economyEndpoints,
  ...leagueEndpoints,
  ...liveEndpoints,
  ...mapEndpoints,
  ...paymentEndpoints,
  ...playerEndpoints,
  ...shareEndpoints,
  ...waveEndpoints,
};

export type Endpoints = typeof endpoints;
export type EndpointName = keyof Endpoints;
/** Тело запроса эндпоинта (как его пишет вызывающий, до значений по умолчанию). */
export type ApiRequest<N extends EndpointName> = z.input<Endpoints[N]["request"]>;
/** Ответ эндпоинта после разбора схемой. */
export type ApiResponse<N extends EndpointName> = z.output<Endpoints[N]["response"]>;

/** Обработчики всех эндпоинтов — форма, которую реализуют сервер, его заглушка и фейковый транспорт клиента. */
export type ApiHandlers<Ctx = void> = {
  [N in EndpointName]: (req: z.output<Endpoints[N]["request"]>, ctx: Ctx) => Promise<ApiResponse<N>>;
};
